import { useEffect, useState } from "react";
import { Users, Mail, Share2, TrendingUp, TrendingDown, DollarSign, MessageSquare, BarChart3, Shield, ExternalLink, Clock, InboxIcon, Minus } from "lucide-react";
import { apiFetch } from "@/lib/api-fetch";
import { PageMeta } from "@/components/PageMeta";

type KpiHistoryPoint = {
  createdAt: string | null;
  leads: number;
  emailsSent: number;
  socialPosts: number;
  deals: number;
  wonDeals: number;
  revenue: number;
};

type PortalData = {
  clientName?: string | null;
  clientEmail?: string | null;
  plan?: string | null;
  branding: {
    agencyName?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    logo?: string | null;
    supportEmail?: string | null;
    supportUrl?: string | null;
    customDomain?: string | null;
  } | null;
  metrics: {
    totalLeads: number;
    emailsSent: number;
    socialPosts: number;
    totalDeals: number;
    wonDeals: number;
    revenue: number;
    chatLeadsCaptured: number;
    kpiLastUpdated?: string | null;
  };
  history?: KpiHistoryPoint[];
};

function Sparkline({ values, color }: { values: number[]; color: string }) {
  if (values.length < 2) return null;

  const W = 64;
  const H = 24;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x},${y}`;
  });

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
      <polyline
        points={pts.join(" ")}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.7"
      />
      <circle
        cx={parseFloat(pts[pts.length - 1].split(",")[0])}
        cy={parseFloat(pts[pts.length - 1].split(",")[1])}
        r="2"
        fill={color}
      />
    </svg>
  );
}

function TrendBadge({ current, previous }: { current: number; previous: number | undefined }) {
  if (previous === undefined) return null;

  if (current > previous) {
    const pct = previous > 0 ? Math.round(((current - previous) / previous) * 100) : null;
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-medium text-emerald-600">
        <TrendingUp className="w-3 h-3" />
        {pct !== null ? `+${pct}%` : "↑"}
      </span>
    );
  }
  if (current < previous) {
    const pct = previous > 0 ? Math.round(((previous - current) / previous) * 100) : null;
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-medium text-red-500">
        <TrendingDown className="w-3 h-3" />
        {pct !== null ? `-${pct}%` : "↓"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-gray-400">
      <Minus className="w-3 h-3" />
      <span>0%</span>
    </span>
  );
}

function PortalMetricCard({
  icon: Icon,
  label,
  value,
  color,
  sparkValues,
  sparkColor,
  current,
  previous,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  color: string;
  sparkValues?: number[];
  sparkColor?: string;
  current: number;
  previous?: number;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-start justify-between mb-4">
        <div className={`inline-flex p-3 rounded-xl ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {sparkValues && sparkValues.length >= 2 && sparkColor && (
          <Sparkline values={sparkValues} color={sparkColor} />
        )}
      </div>
      <p className="text-sm text-gray-500 font-medium mb-1">{label}</p>
      <div className="flex items-end gap-2">
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        <div className="mb-0.5">
          <TrendBadge current={current} previous={previous} />
        </div>
      </div>
    </div>
  );
}

export function ClientPortal({ portalToken }: { portalToken: string }) {
  const [data, setData] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (!portalToken) {
        setError("No portal token provided. Please use the link shared by your agency.");
        setLoading(false);
        return;
      }
      try {
        const resp = await apiFetch(`/api/portal/${portalToken}`);
        if (resp.status === 404) {
          setError("This portal link is invalid or has expired. Please contact your agency for a new link.");
          setLoading(false);
          return;
        }
        if (!resp.ok) throw new Error("Failed to load portal");
        const json = await resp.json();
        setData(json);
      } catch {
        setError("Could not load client report. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [portalToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">{error ?? "Portal not available."}</p>
        </div>
      </div>
    );
  }

  const { branding, metrics, history = [] } = data;
  const primaryColor = branding?.primaryColor || "#6366f1";
  const agencyName = branding?.agencyName || "Growth Platform";

  const fmt = (n: number) => n.toLocaleString();
  const fmtMoney = (n: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  const hasKpis =
    metrics.totalLeads > 0 ||
    metrics.emailsSent > 0 ||
    metrics.socialPosts > 0 ||
    metrics.totalDeals > 0 ||
    metrics.wonDeals > 0 ||
    metrics.revenue > 0 ||
    metrics.chatLeadsCaptured > 0;

  const fmtTimestamp = (ts: string | null | undefined): string => {
    if (!ts) return "";
    try {
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(ts));
    } catch {
      return ts;
    }
  };

  const prevSnapshot = history.length >= 2 ? history[history.length - 2] : undefined;

  const spark = (key: keyof KpiHistoryPoint) =>
    history.length >= 2 ? history.map(h => h[key] as number) : undefined;

  return (
    <>
      <PageMeta
        title={data ? `${data.clientName ?? "Client"} — Growth Report — LaunchTactix` : "Client Portal — LaunchTactix"}
        description="View your personalised AI growth platform report — leads, outreach, social posts, and pipeline performance."
        canonical="https://launchtactix.com/portal"
      />
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          {branding?.logo ? (
            <img src={branding.logo} alt={agencyName} width={200} height={60} className="h-9 object-contain" />
          ) : (
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ background: primaryColor }}
            >
              {agencyName.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="text-lg font-bold text-gray-900">{agencyName}</h1>
            <p className="text-xs text-gray-400">Client Performance Report</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-gray-400">Read-only view</span>
            {branding?.supportEmail && (
              <a
                href={`mailto:${branding.supportEmail}`}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Contact Support
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Performance Overview</h2>
            <p className="text-gray-500 mt-1">
              A summary of your campaign results from {agencyName}.
            </p>
          </div>
          {metrics.kpiLastUpdated && (
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Clock className="w-3.5 h-3.5" />
              <span>Updated {fmtTimestamp(metrics.kpiLastUpdated)}</span>
            </div>
          )}
        </div>

        {!hasKpis ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 flex flex-col items-center text-center mb-10">
            <div className="inline-flex p-4 rounded-2xl bg-gray-100 mb-4">
              <InboxIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-700 mb-2">No metrics yet</h3>
            <p className="text-sm text-gray-400 max-w-sm">
              Your agency hasn&apos;t published any campaign results yet. Check back soon — your KPIs will appear here once they&apos;ve been updated.
            </p>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 mb-10">
          <PortalMetricCard
            icon={Users}
            label="Total Leads"
            value={fmt(metrics.totalLeads)}
            color="bg-blue-500"
            sparkValues={spark("leads")}
            sparkColor="#3b82f6"
            current={metrics.totalLeads}
            previous={prevSnapshot?.leads}
          />
          <PortalMetricCard
            icon={Mail}
            label="Emails Sent"
            value={fmt(metrics.emailsSent)}
            color="bg-indigo-500"
            sparkValues={spark("emailsSent")}
            sparkColor="#6366f1"
            current={metrics.emailsSent}
            previous={prevSnapshot?.emailsSent}
          />
          <PortalMetricCard
            icon={Share2}
            label="Social Posts Published"
            value={fmt(metrics.socialPosts)}
            color="bg-pink-500"
            sparkValues={spark("socialPosts")}
            sparkColor="#ec4899"
            current={metrics.socialPosts}
            previous={prevSnapshot?.socialPosts}
          />
          <PortalMetricCard
            icon={TrendingUp}
            label="Total Deals"
            value={fmt(metrics.totalDeals)}
            color="bg-amber-500"
            sparkValues={spark("deals")}
            sparkColor="#f59e0b"
            current={metrics.totalDeals}
            previous={prevSnapshot?.deals}
          />
          <PortalMetricCard
            icon={BarChart3}
            label="Won Deals"
            value={fmt(metrics.wonDeals)}
            color="bg-green-500"
            sparkValues={spark("wonDeals")}
            sparkColor="#22c55e"
            current={metrics.wonDeals}
            previous={prevSnapshot?.wonDeals}
          />
          <PortalMetricCard
            icon={DollarSign}
            label="Closed Revenue"
            value={fmtMoney(metrics.revenue)}
            color="bg-emerald-600"
            sparkValues={spark("revenue")}
            sparkColor="#059669"
            current={metrics.revenue}
            previous={prevSnapshot?.revenue}
          />
          <PortalMetricCard
            icon={MessageSquare}
            label="Chat Leads Captured"
            value={fmt(metrics.chatLeadsCaptured)}
            color="bg-violet-500"
            current={metrics.chatLeadsCaptured}
            previous={undefined}
          />
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Campaign Health Summary</h3>
          <div className="space-y-4">
            {[
              {
                label: "Outreach Coverage",
                pct: metrics.totalLeads > 0 ? Math.min(100, Math.round((metrics.emailsSent / metrics.totalLeads) * 100)) : 0,
                color: "#6366f1",
              },
              {
                label: "Deal Win Rate",
                pct: metrics.totalDeals > 0 ? Math.round((metrics.wonDeals / metrics.totalDeals) * 100) : 0,
                color: "#22c55e",
              },
              {
                label: "Social Presence",
                pct: Math.min(100, metrics.socialPosts * 5),
                color: "#ec4899",
              },
            ].map((row) => (
              <div key={row.label}>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">{row.label}</span>
                  <span className="text-sm font-semibold text-gray-900">{row.pct}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{ width: `${row.pct}%`, background: row.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
          </>
        )}

        {branding?.supportUrl && (
          <div className="mt-8 text-center">
            <a
              href={branding.supportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Help Center
            </a>
          </div>
        )}
      </main>

      {!branding && (
        <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-xs text-gray-500">
          Powered by LaunchTactix
        </div>
      )}
    </div>
    </>
  );
}

export function ClientPortalPage({ params }: { params: { portalToken?: string } }) {
  return <ClientPortal portalToken={params.portalToken ?? ""} />;
}
